import { tintOnWhite } from '@/theme/colorUtils';
import { isLightTheme } from '@/theme/semanticTokens';
import { lightModeStatusColors, uiTokens } from '@/theme/uiTokens';
import type { UiTone } from '@/theme/uiTokens';
import type { StatusTone, Theme } from '@/ui/primitives';
import type { BuildStatus, CiDisplayStatusKey } from '@flowqa/shared';

export type TableMetricAccentToneInput = StatusTone | UiTone;
export type TableActionIconAccentTone = Extract<StatusTone, 'error' | 'success'>;
export type TableMetricDeltaDirection = 'higherBetter' | 'lowerBetter';
export type TableStatusBreakdownStatus = BuildStatus | CiDisplayStatusKey;

export const tableMetricAccentLightModeAlphaOnWhite = 0.82;

export const resolveTableMetricAccentTone = (tone: TableMetricAccentToneInput): UiTone => {
  if (tone === 'success' || tone === 'green') return 'green';
  if (tone === 'error' || tone === 'red') return 'red';
  if (tone === 'warning' || tone === 'orange' || tone === 'yellow') return 'orange';
  if (tone === 'info' || tone === 'blue') return 'blue';
  if (tone === 'primary' || tone === 'teal') return 'teal';
  if (tone === 'purple') return 'purple';
  if (tone === 'brown') return 'brown';
  if (tone === 'pink') return 'pink';
  return 'neutral';
};

export const resolveTableMetricAccentColor = (
  theme: Theme,
  tone: TableMetricAccentToneInput,
): string => {
  const statusTone = resolveTableMetricAccentTone(tone);
  if (isLightTheme(theme)) {
    return tintOnWhite(lightModeStatusColors[statusTone], tableMetricAccentLightModeAlphaOnWhite);
  }
  return uiTokens.statusColors[statusTone];
};

export const resolveTableMetricDeltaAccentTone = (
  direction: TableMetricDeltaDirection,
  deltaValue: number,
): UiTone => {
  if (deltaValue === 0) return 'neutral';
  const isImproving = direction === 'higherBetter' ? deltaValue > 0 : deltaValue < 0;
  return isImproving ? 'green' : 'red';
};

export const resolveTableMetricDeltaAccentColor = (
  theme: Theme,
  direction: TableMetricDeltaDirection,
  deltaValue: number,
): string =>
  resolveTableMetricAccentColor(theme, resolveTableMetricDeltaAccentTone(direction, deltaValue));

export const resolveTableActionIconAccentColor = (
  theme: Theme,
  tone: TableActionIconAccentTone,
): string => resolveTableMetricAccentColor(theme, tone);

export const resolveTableStatusBreakdownAccentTone = (
  status: TableStatusBreakdownStatus,
): UiTone | null => {
  if (status === 'completed' || status === 'success') return 'green';
  if (status === 'failed' || status === 'canceled' || status === 'timed_out') return 'red';
  if (status === 'running') return 'blue';
  if (status === 'warning' || status === 'skipped' || status === 'action_required') {
    return 'orange';
  }
  return null;
};

export const resolveTableStatusBreakdownIconColor = (
  theme: Theme,
  status: TableStatusBreakdownStatus,
): string => {
  const accentTone = resolveTableStatusBreakdownAccentTone(status);
  if (accentTone === null) return 'text.secondary';
  return resolveTableMetricAccentColor(theme, accentTone);
};
